import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import SoftBadge from 'components/common/SoftBadge';
import { formatMoney, parseRouteParams } from 'helpers/utils';
import paths from 'routes/paths';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { usePledges } from 'hooks/usePledges';
import PledgesFilterForm from './PledgesFilterForm';
import useSessionState from 'hooks/useSessionState';
import {
  PLEDGE_STATUSES,
  PLEDGE_FREQUENCIES,
  PLEDGE_SOURCES,
  pledgeRef,
  donorFullName
} from './pledgeConstants';

// Order statuses that count as money received (matches sumPledgePaid).
const PAID_ORDER_STATUSES = ['paid', 'partially_refunded'];

const columns = [
  {
    accessor: 'ref',
    Header: 'ID',
    cellProps: { className: 'py-2 fw-semi-bold' }
  },
  {
    accessor: 'donorName',
    Header: 'Donor',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { donorName, donorId } = rowData.row.original;
      return donorId ? (
        <Link to={`/donors/${donorId}`} className="fw-medium">
          {donorName}
        </Link>
      ) : (
        donorName
      );
    }
  },
  {
    // Event replaces the old Campaign column (no room for both): event
    // pledges show the event, the rest fall back to their campaign.
    accessor: 'eventOrCampaign',
    Header: 'Event / Campaign',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { eventName, eventDate, campaign } = rowData.row.original;
      if (eventName) {
        return (
          <div>
            <div className="fw-medium">{eventName}</div>
            {eventDate && (
              <div className="text-500 fs--2">
                {dayjs(eventDate).format('DD MMM YYYY')}
              </div>
            )}
          </div>
        );
      }
      return campaign || '—';
    }
  },
  {
    accessor: 'source',
    Header: 'Source',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { source } = rowData.row.original;
      return source ? PLEDGE_SOURCES[source] || source : '—';
    }
  },
  {
    accessor: 'totalValue',
    Header: 'Pledged',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { totalValue, amount, frequency } = rowData.row.original;
      if (totalValue) return formatMoney(totalValue);
      return `${formatMoney(amount)}/${
        frequency === 'annually' ? 'yr' : 'mo'
      } (open)`;
    }
  },
  {
    accessor: 'paidTotal',
    Header: 'Paid',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { paidTotal, paidOrders, donorId, id } = rowData.row.original;
      if (!paidTotal || !paidOrders?.length || !donorId) {
        return formatMoney(paidTotal);
      }
      // One payment: deep-link straight to that donation on the donor profile.
      // Several payments: the pledge profile lists them all.
      const to =
        paidOrders.length === 1
          ? `/donors/${donorId}/giving/${paidOrders[0].id}`
          : parseRouteParams(paths.pledgeProfile, { id });
      return (
        <Link to={to} className="fw-medium">
          {formatMoney(paidTotal)}
        </Link>
      );
    }
  },
  {
    accessor: 'outstanding',
    Header: 'Outstanding',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { outstanding } = rowData.row.original;
      return outstanding === null ? '—' : formatMoney(outstanding);
    }
  },
  {
    accessor: 'frequencyLabel',
    Header: 'Frequency',
    cellProps: { className: 'py-2' }
  },
  {
    accessor: 'followUpDate',
    Header: 'Follow-Up',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { followUpDate, statusKey } = rowData.row.original;
      if (!followUpDate) return '—';
      const overdue =
        ['new', 'awaiting_follow_up'].includes(statusKey) &&
        dayjs(followUpDate).isBefore(dayjs(), 'day');
      return (
        <span className={overdue ? 'text-danger fw-semi-bold' : ''}>
          {dayjs(followUpDate).format('DD/MM/YYYY')}
        </span>
      );
    }
  },
  {
    accessor: 'statusLabel',
    Header: 'Status',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { statusKey } = rowData.row.original;
      const status = PLEDGE_STATUSES[statusKey] || {
        label: statusKey,
        bg: 'secondary'
      };
      return (
        <SoftBadge pill bg={status.bg}>
          {status.label}
        </SoftBadge>
      );
    }
  },
  {
    accessor: 'assignedStaff',
    Header: 'Assigned',
    cellProps: { className: 'py-2' }
  }
];

// Filter-bar values -> list filters
const toFilters = data => ({
  ...(data.status?.length && { status: data.status }),
  ...(data.frequency?.length && { frequency: data.frequency }),
  ...(data.campaign?.length && { campaign: data.campaign }),
  ...(data.source?.length && { source: data.source }),
  ...(data.priority?.length && { priority: data.priority }),
  ...(data.assigned?.length && { assigned: data.assigned }),
  ...(data.followUp?.from && { followUpFrom: data.followUp.from }),
  ...(data.followUp?.to && { followUpTo: data.followUp.to })
});

const AllPledges = () => {
  const navigate = useNavigate();
  const { pledges, isLoading } = usePledges();
  // The filter bar's values are kept for this tab, so coming back to the
  // list shows the same filters and results
  const [savedFilterForm, setSavedFilterForm] = useSessionState(
    'filters:pledges',
    null
  );
  const [filters, setFilters] = useState(() =>
    savedFilterForm ? toFilters(savedFilterForm) : {}
  );

  const assignedOptions = useMemo(() => {
    const seen = new Map();
    pledges.forEach(p => {
      if (p.assigned_user?.id && !seen.has(p.assigned_user.id)) {
        seen.set(p.assigned_user.id, donorFullName(p.assigned_user));
      }
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [pledges]);

  const filteredPledges = useMemo(() => {
    let list = pledges;
    if (filters.status?.length) {
      list = list.filter(p => filters.status.includes(p.effectiveStatus));
    }
    if (filters.frequency?.length) {
      list = list.filter(p => filters.frequency.includes(p.frequency));
    }
    if (filters.campaign?.length) {
      list = list.filter(p => filters.campaign.includes(p.package_id));
    }
    if (filters.source?.length) {
      list = list.filter(p => filters.source.includes(p.source));
    }
    if (filters.priority?.length) {
      list = list.filter(p => filters.priority.includes(p.priority));
    }
    if (filters.assigned?.length) {
      list = list.filter(p => filters.assigned.includes(p.assigned_to));
    }
    if (filters.followUpFrom) {
      list = list.filter(
        p =>
          p.follow_up_date &&
          !dayjs(p.follow_up_date).isBefore(dayjs(filters.followUpFrom), 'day')
      );
    }
    if (filters.followUpTo) {
      list = list.filter(
        p =>
          p.follow_up_date &&
          !dayjs(p.follow_up_date).isAfter(dayjs(filters.followUpTo), 'day')
      );
    }
    return list;
  }, [pledges, filters]);

  const tableData = useMemo(
    () =>
      filteredPledges.map(pledge => ({
        id: pledge.id,
        ref: pledgeRef(pledge),
        donorName: donorFullName(pledge.donor),
        donorId: pledge.donor?.id,
        campaign: pledge.package?.package_title || '—',
        eventName: pledge.event_name || '',
        source: pledge.source || '',
        eventDate: pledge.event_date || null,
        eventOrCampaign:
          pledge.event_name || pledge.package?.package_title || '—',
        amount: Number(pledge.amount),
        totalValue: pledge.total_value ? Number(pledge.total_value) : null,
        paidTotal: pledge.paidTotal,
        paidOrders: (pledge.orders || []).filter(o =>
          PAID_ORDER_STATUSES.includes(o.payment_status)
        ),
        outstanding: pledge.outstanding,
        frequencyLabel:
          PLEDGE_FREQUENCIES[pledge.frequency] || pledge.frequency,
        frequency: pledge.frequency,
        followUpDate: pledge.follow_up_date,
        statusKey: pledge.effectiveStatus,
        statusLabel: PLEDGE_STATUSES[pledge.effectiveStatus]?.label || '',
        assignedStaff: donorFullName(pledge.assigned_user)
      })),
    [filteredPledges]
  );

  const onFilterClick = data => {
    setSavedFilterForm(data);
    setFilters(toFilters(data));
  };

  return (
    <>
      <div className="card shadow-none border mb-4">
        <div className="card-body">
          <PledgesFilterForm
            onFilter={onFilterClick}
            initialValues={savedFilterForm}
            assignedOptions={assignedOptions}
          />
        </div>
      </div>
      <div
        className="cursor-pointer"
        onClick={e => {
          // Row navigation: let inner links (donor, paid) behave normally
          const row = e.target.closest('tr');
          if (!row || e.target.closest('a, button')) return;
          const ref = row.cells?.[0]?.textContent;
          const match = tableData.find(d => d.ref === ref);
          if (match) {
            navigate(parseRouteParams(paths.pledgeProfile, { id: match.id }));
          }
        }}
      >
        <CustomAdvanceTable
          data={tableData}
          columns={columns}
          title="All Pledges"
          addButtonLabel="Add New Pledge"
          subject={permissionsKeyMap.pledge.children.pledges}
          showSearchInput
          searchInputPlaceHolder="Search by donor, campaign, ref..."
          onClickAddNew={() => navigate(paths.addPledge)}
          loading={isLoading}
          addNew
        />
      </div>
    </>
  );
};

export default AllPledges;
